import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../services/api';
import type { Farm } from '../../types';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link as MuiLink,
  Divider,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  IconButton,
} from '@mui/material';
import { Home as HomeIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';

interface FarmFormValues {
  id?: string;
  name: string;
  location: string;
  size: string;
  user_id?: string;
  created_at?: string;
}

const FarmForm: React.FC = () => {
  const { farmId } = useParams<{ farmId?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [farm, setFarm] = useState<FarmFormValues>({ 
    name: '',
    location: '',
    size: 'small'
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formError, setFormError] = useState<Record<string, string>>({});

  // Load farm data if in edit mode
  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        
        if (farmId) {
          // Fetch existing farm
          const { data: farmData, error: farmError } = await api.farms.getById(farmId);

          if (farmError) throw farmError;
          if (!farmData) throw new Error('Farm not found');
          
          setFarm(farmData);
        }
      } catch (error) {
        console.error('Error loading farm:', error);
        setError('Failed to load farm data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, farmId]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFarm(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field if it exists
    if (formError[name]) {
      setFormError(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setFarm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (!farm.name.trim()) {
      errors.name = 'Farm name is required';
    }
    
    if (!farm.location.trim()) {
      errors.location = 'Location is required';
    }
    
    setFormError(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm() || !user) return;
    
    try {
      setSaving(true);
      
      if (farmId && typeof farmId === 'string') {
        // Update existing farm
        const { error } = await api.farms.update(farmId, farm.name);
        if (error) throw error;
        // Navigate back to farm detail
        navigate(`/farms/${farmId}`);
      } else {
        // Create new farm
        const { data, error } = await api.farms.create(farm.name);
        if (error) throw error;
        if (data && data[0]) {
          // Navigate to the new farm's detail page
          navigate(`/farms/${data[0].id}`);
        }
      }
    } catch (error) {
      console.error('Error saving farm:', error);
      setError('Failed to save farm. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Breadcrumb Navigation */}
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
        <MuiLink
          component={Link}
          to="/farms"
          color="inherit"
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Farms
        </MuiLink>
        <Typography color="text.primary">
          {farmId ? 'Edit Farm' : 'Add New Farm'}
        </Typography>
      </Breadcrumbs>

      {/* Header with back button */}
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton 
          onClick={() => navigate(-1)} 
          sx={{ mr: 1 }}
          aria-label="go back"
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          {farmId ? 'Edit Farm' : 'Add New Farm'}
        </Typography>
      </Box>

      <Paper sx={{ p: 4, mb: 4 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                margin="normal"
                label="Farm Name"
                name="name"
                value={farm.name}
                onChange={handleInputChange}
                error={!!formError.name}
                helperText={formError.name || 'Enter a name for your farm'}
                required
                disabled={saving}
              />
            </Grid>
            
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                margin="normal"
                label="Location"
                name="location"
                value={farm.location}
                onChange={handleInputChange}
                error={!!formError.location}
                helperText={formError.location || 'Enter the location of your farm'}
                required
                disabled={saving}
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <FormControl fullWidth margin="normal">
                <InputLabel id="farm-size-label">Farm Size</InputLabel>
                <Select
                  labelId="farm-size-label"
                  id="size"
                  name="size"
                  value={farm.size}
                  label="Farm Size"
                  onChange={handleSelectChange}
                  disabled={saving}
                >
                  <MenuItem value="small">Small (0-1 ha)</MenuItem>
                  <MenuItem value="medium">Medium (1-5 ha)</MenuItem>
                  <MenuItem value="large">Large (5+ ha)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
                <Button
                  variant="outlined"
                  onClick={() => navigate(-1)}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={saving}
                  startIcon={saving ? <CircularProgress size={20} /> : null}
                >
                  {saving ? 'Saving...' : farmId ? 'Update Farm' : 'Create Farm'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
      
      {farmId && (
        <Box>
          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" color="error" gutterBottom>
            Danger Zone
          </Typography>
          <Paper sx={{ p: 3, backgroundColor: 'rgba(255, 0, 0, 0.02)', border: '1px solid rgba(255, 0, 0, 0.1)' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="subtitle1" color="error">
                  Delete this farm
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Warning: This will permanently delete the farm and all associated zones and data.
                  This action cannot be undone.
                </Typography>
              </Box>
              <Button
                variant="outlined"
                color="error"
                onClick={() => navigate(`/farms/${farmId}`, { state: { showDelete: true } })}
              >
                Delete Farm
              </Button>
            </Box>
          </Paper>
        </Box>
      )}
    </Container>
  );
};

export default FarmForm;
